import type { Collab, Campaign, CreatorBountyClaim } from '@foodfluence/shared';
import { calculateCollabNetPayoutUsd, calculateCreatorNetPayoutUsd } from '@foodfluence/shared';
import type { WalletPendingPayoutItem } from '@foodfluence/shared';
import { getPendingCategoryLabel } from '@foodfluence/shared';
import type { LcOrderItem } from '../../../services/api/wallet';
import { resolveCollabProductValue } from '@foodfluence/shared';
import {
  formatCompensationBreakdown,
  getCollabCompensationBreakdown,
  type WalletCompensationLine,
} from './walletCompensationUtils';

type TimestampLike =
  | {
      toDate?: () => Date;
      seconds?: number;
      _seconds?: number;
      nanoseconds?: number;
      _nanoseconds?: number;
    }
  | string;

function toDate(dateLike: TimestampLike | null | undefined): Date | null {
  if (!dateLike) return null;
  if (typeof dateLike === 'string') {
    const parsed = new Date(dateLike);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof dateLike.toDate === 'function') return dateLike.toDate();
  const seconds = dateLike.seconds ?? dateLike._seconds;
  if (typeof seconds === 'number') {
    const nanos = dateLike.nanoseconds ?? dateLike._nanoseconds ?? 0;
    return new Date(seconds * 1000 + nanos / 1_000_000);
  }
  return null;
}

/** Figma hero balance — e.g. $1,284.50 */
export function formatWalletBalance(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export interface WalletActivityItem {
  id: string;
  kind: 'collab' | 'lc_order' | 'bounty';
  collabId?: string;
  title: string;
  subtitle: string;
  compensationBreakdown: string;
  compensationLines: WalletCompensationLine[];
  amount: number;
  date: Date;
  logoUrl?: string;
  payoutStatusLabel?: string;
  /** Collab/LC cash rows net of platform fee; bounties pay out in full. */
  showFeeHint?: boolean;
}


/** Cash (net of 5% fee) + full retail product value — matches backend wallet bootstrap transfers. */
export function collabWithdrawableAmount(collab: Collab, campaign?: Campaign): number {
  const cashUsd = collab.cash ?? 0;
  // Must equal what the backend transfers (WalletService), which credits the recorded selection.
  const productValueUsd =
    campaign?.campaignType === 'retail' ? resolveCollabProductValue(collab, campaign) : 0;
  return calculateCollabNetPayoutUsd(cashUsd, productValueUsd);
}

/** Full bounty amount — no platform fee (unlike collab/LC cash payouts). */
export function calculateUnpaidBountyTotal(
  claims: Pick<CreatorBountyClaim, 'status' | 'payoutAmount' | 'payoutStatus'>[] = [],
): number {
  return claims
    .filter((c) => c.status === 'verified' && c.payoutStatus !== 'paid')
    .reduce((sum, c) => sum + (c.payoutAmount ?? 0), 0);
}

/** Unclaimed completed collabs + LC orders + verified bounties — what moves to Whop on wallet open. */
export function calculatePendingPayoutTotal(
  collabs: Collab[],
  campaignByCollabId: Record<string, Campaign>,
  lcOrders: LcOrderItem[] = [],
  bountyClaims: Pick<CreatorBountyClaim, 'status' | 'payoutAmount' | 'payoutStatus'>[] = [],
): number {
  const collabTotal = collabs
    .filter((c) => c.status === 'collabCompleted' && !c.paymentClaimed)
    .reduce((sum, collab) => sum + collabWithdrawableAmount(collab, campaignByCollabId[collab.id]), 0);

  const lcTotal = lcOrders
    .filter((o) => !o.payoutClaimed)
    .reduce((sum, order) => sum + calculateCreatorNetPayoutUsd(order.cash), 0);

  return collabTotal + lcTotal + calculateUnpaidBountyTotal(bountyClaims);
}

export function enrichActivityWithPayoutStatus(
  activities: WalletActivityItem[],
  pendingItems: WalletPendingPayoutItem[],
): WalletActivityItem[] {
  if (pendingItems.length === 0) {
    return activities;
  }

  const categoryByActivityId = new Map(pendingItems.map((item) => [item.id, item.category]));

  return activities.map((activity) => {
    const category = categoryByActivityId.get(activity.id);
    if (!category) {
      return activity;
    }
    return {
      ...activity,
      payoutStatusLabel: getPendingCategoryLabel(category),
    };
  });
}

function collabCompletedDate(collab: Collab): Date | null {
  return (
    toDate(collab.logs?.find((l) => l.status === 'collabCompleted')?.timestamp as Parameters<typeof toDate>[0]) ??
    toDate(collab.collabRedeemedTime as Parameters<typeof toDate>[0])
  );
}

type WalletBountyActivityClaim = Pick<
  CreatorBountyClaim,
  'id' | 'bountyID' | 'companyName' | 'status' | 'payoutAmount' | 'payoutStatus' | 'verifiedTime' | 'paidOutTime'
>;

export function buildWalletActivity(
  collabs: Collab[],
  campaignData: Record<string, Campaign>,
  clients: Record<string, { accountName?: string; instagramProfilePicture?: string }>,
  lcOrders: LcOrderItem[],
  bountyClaims: WalletBountyActivityClaim[] = [],
  bountyLogoByBountyId: Record<string, string | undefined> = {},
  limit = 5,
): WalletActivityItem[] {
  const items: WalletActivityItem[] = [];

  collabs
    .filter((c) => c.status === 'collabCompleted')
    .forEach((collab) => {
      const date = collabCompletedDate(collab);
      if (!date) return;
      const campaign = campaignData[collab.id];
      const client = campaign?.clientID ? clients[campaign.clientID] : undefined;
      const compensationLines = getCollabCompensationBreakdown(collab, campaign);
      items.push({
        id: `collab-${collab.id}`,
        kind: 'collab',
        collabId: collab.id,
        title: client?.accountName ?? 'Brand collaboration',
        subtitle: 'Collaboration completed',
        compensationBreakdown: formatCompensationBreakdown(compensationLines),
        compensationLines,
        amount: collabWithdrawableAmount(collab, campaign),
        date,
        logoUrl: client?.instagramProfilePicture,
        showFeeHint: true,
      });
    });

  lcOrders.forEach((order) => {
    const compensationLines: WalletCompensationLine[] = [
      { kind: 'cash', label: 'Cash', amount: order.cash, withdrawable: true },
    ];
    items.push({
      id: `lc-${order.id}`,
      kind: 'lc_order',
      title: order.brandName,
      subtitle: order.payoutClaimed
        ? 'Local Creators order completed'
        : 'Local Creators order — payout pending',
      compensationBreakdown: formatCompensationBreakdown(compensationLines),
      compensationLines,
      amount: calculateCreatorNetPayoutUsd(order.cash),
      date: new Date(order.completedAt),
      logoUrl: order.brandLogoUrl,
      showFeeHint: true,
    });
  });

  bountyClaims
    .filter((claim) => claim.status === 'verified' && typeof claim.payoutAmount === 'number' && claim.payoutAmount > 0)
    .forEach((claim) => {
      const date = toDate(claim.paidOutTime as Parameters<typeof toDate>[0])
        ?? toDate(claim.verifiedTime as Parameters<typeof toDate>[0]);
      if (!date) return;

      items.push({
        id: `bounty-${claim.id}`,
        kind: 'bounty',
        title: claim.companyName,
        subtitle: claim.payoutStatus === 'paid'
          ? 'Bounty payout completed'
          : 'Bounty verified — payout pending',
        compensationBreakdown: '',
        compensationLines: [],
        amount: claim.payoutAmount!,
        date,
        logoUrl: bountyLogoByBountyId[claim.bountyID],
        showFeeHint: false,
      });
    });

  return items
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .slice(0, limit);
}

export function formatActivityDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
